import { useParams, Link, Navigate } from "react-router-dom";
import { Check, Phone, ArrowRight, Award, Wrench, Sparkles, ImagePlus, ClipboardCheck } from "lucide-react";
import { SERVICES } from "@/data/services";
import { SITE } from "@/data/site";
import { Seo } from "@/components/Seo";
import { Reveal, RevealStagger } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { BeforeAfter } from "@/components/BeforeAfter";
import { FaqAccordion } from "@/components/FaqAccordion";
import { CtaFinal } from "@/components/CtaFinal";

const approcheIcons = [Award, Wrench, Sparkles];

export default function ServicePage({ slug }) {
  const params = useParams();
  const service = SERVICES[slug || params.slug];
  if (!service) return <Navigate to="/" replace />;

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.h1,
    provider: { "@id": "https://hrcrenovation.fr/#business" },
    areaServed: ["Dordogne", "Lot", "Corrèze", "Haute-Vienne", "Charente"],
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://hrcrenovation.fr/" },
      { "@type": "ListItem", position: 2, name: service.h1 },
    ],
  };

  return (
    <>
      <Seo title={service.title} description={service.metaDescription} jsonLd={[serviceLd, faqLd, breadcrumbLd]} />

      {/* Hero service */}
      <section
        className="relative noise-overlay pt-[120px] pb-16 lg:pb-24"
        style={{ background: "var(--surface-canvas)", backgroundImage: "radial-gradient(circle at 100% 0%, rgba(30,79,168,0.08) 0%, transparent 50%)" }}
        data-testid="service-hero"
      >
        <div className="max-w-site mx-auto px-5 lg:px-6">
          <nav className="text-[13px] text-ink-tertiary mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-brand-royal">Accueil</Link>
            <span className="mx-2">/</span>
            <span className="text-ink-secondary">{service.h1}</span>
          </nav>
          <Reveal>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-deep max-w-[820px]" style={{ letterSpacing: "-0.03em" }} data-testid="service-h1">
              {service.h1}
            </h1>
            <p className="mt-6 text-lg text-ink-secondary max-w-[600px]">{service.subtitle}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/devis" className="btn-base btn-metal w-full sm:w-auto" data-testid="service-devis-btn">
                Obtenir mon devis gratuit
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <a href={SITE.phoneHref} className="btn-base btn-outline-deep w-full sm:w-auto tnum">
                <Phone size={18} aria-hidden="true" />
                {SITE.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.2} className="mt-12">
            <div className="rounded-2xl overflow-hidden shadow-elevated border border-line">
              {service.heroImage ? (
                <img
                  src={service.heroImage}
                  alt={service.heroImageAlt}
                  loading="lazy"
                  className="w-full object-cover"
                  style={{ aspectRatio: "21/9", minHeight: 220 }}
                />
              ) : (
                /* TODO: Remplacer par photo réelle chantier Brandon.
                    NE PAS générer d'image IA. NE PAS utiliser d'image stock. */
                <div className="photo-placeholder" style={{ aspectRatio: "21/9", minHeight: 220 }}>
                  <span className="w-11 h-11 rounded-full bg-brand-highlight flex items-center justify-center">
                    <ImagePlus size={20} className="text-brand-royal" aria-hidden="true" />
                  </span>
                  <span className="font-medium text-ink-secondary">Photo à venir</span>
                  <span className="dashed-zone">{service.heroAlt}</span>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Checklist */}
      <section className="py-16 lg:py-28 bg-surface-canvas" data-testid="service-checklist">
        <div className="max-w-site mx-auto px-5 lg:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
          <Reveal
            className="relative overflow-hidden rounded-2xl border border-line p-8 lg:p-10 flex flex-col justify-center"
            style={{ background: "linear-gradient(135deg, var(--brand-highlight) 0%, var(--surface-muted) 100%)" }}
          >
            <ClipboardCheck
              size={220}
              strokeWidth={1}
              className="absolute -right-10 -bottom-10 text-brand-royal/10 pointer-events-none"
              aria-hidden="true"
            />
            <p className="micro-label mb-4 relative">Le détail</p>
            <h2 className="font-heading font-bold text-3xl lg:text-[40px] leading-[1.1] text-brand-deep relative">Ce qu'on fait pour vous.</h2>
            <p className="mt-5 text-ink-secondary max-w-[440px] relative">
              Chaque chantier commence par une visite gratuite chez vous. Voici ce que couvre notre intervention.
            </p>
          </Reveal>
          <RevealStagger className="space-y-4">
            {service.checklist.map((item) => (
              <div key={item} className="flex items-start gap-4 bg-white border border-line rounded-xl px-5 py-4">
                <span className="w-6 h-6 rounded-full bg-brand-highlight flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={14} className="text-brand-royal" strokeWidth={3} aria-hidden="true" />
                </span>
                <span className="text-[15px] text-ink font-medium">{item}</span>
              </div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Chantier réel complémentaire (optionnel, seulement si service.secondaryImage est défini) */}
      {service.secondaryImage && (
        <section className="py-16 lg:py-28 bg-surface-canvas" data-testid="service-secondary-image">
          <div className="max-w-site mx-auto px-5 lg:px-6 grid grid-cols-1 lg:grid-cols-[45fr_55fr] gap-12 items-center">
            <Reveal>
              <div className="rounded-2xl overflow-hidden shadow-elevated border border-line max-w-[360px] mx-auto lg:mx-0">
                <img
                  src={service.secondaryImage}
                  alt={service.secondaryImageAlt}
                  loading="lazy"
                  className="w-full object-cover"
                  style={{ aspectRatio: "4/5" }}
                />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="micro-label mb-4">Aussi</p>
              <h2 className="font-heading font-bold text-3xl lg:text-[40px] leading-[1.1] text-brand-deep">{service.secondaryTitle}</h2>
              <p className="mt-5 text-ink-secondary max-w-[440px]">{service.secondaryText}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* Approche */}
      <section className="py-16 lg:py-28 bg-brand-deep noise-overlay relative" data-testid="service-approche">
        <div className="max-w-site mx-auto px-5 lg:px-6 relative">
          <SectionHeader label="Notre approche" title="Pourquoi le résultat tient dans le temps." dark />
          <RevealStagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {service.approche.map((a, i) => {
              const Icon = approcheIcons[i];
              return (
                <div key={a.title} className="bg-white rounded-2xl p-8 border border-line shadow-subtle">
                  <div className="w-14 h-14 rounded-xl bg-brand-highlight flex items-center justify-center mb-6">
                    <Icon size={26} className="text-brand-royal" aria-hidden="true" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-brand-deep">{a.title}</h3>
                  <p className="mt-3 text-[15px] text-ink-secondary">{a.text}</p>
                </div>
              );
            })}
          </RevealStagger>
        </div>
      </section>

      {/* Avant / Après */}
      <section className="py-16 lg:py-28 bg-surface-canvas" data-testid="service-avant-apres">
        <div className="max-w-site mx-auto px-5 lg:px-6">
          <SectionHeader label="Avant / Après" title="Jugez sur pièces." subtitle="Faites glisser le curseur pour voir la différence." />
          <RevealStagger className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[820px] mx-auto">
            {/* TODO: Remplacer par photo réelle chantier Brandon.
                NE PAS générer d'image IA. NE PAS utiliser d'image stock. */}
            <div className="rounded-xl overflow-hidden border border-line shadow-subtle"><BeforeAfter alt={service.heroAlt} /></div>
            <div className="rounded-xl overflow-hidden border border-line shadow-subtle"><BeforeAfter alt={service.heroAlt} /></div>
          </RevealStagger>
        </div>
      </section>

      {/* Devis */}
      <section className="py-16 lg:py-24 bg-surface-canvas" data-testid="service-tarifs">
        <div className="max-w-[780px] mx-auto px-5 lg:px-6">
          <Reveal className="bg-brand-deep rounded-2xl p-8 lg:p-12 text-center relative noise-overlay overflow-hidden">
            <p className="micro-label mb-3" style={{ color: "rgba(255,255,255,0.6)" }}>Devis gratuit</p>
            <p className="font-heading font-bold text-3xl lg:text-4xl text-white">Un chiffrage précis, chez vous, sous 48h.</p>
            <p className="mt-4 text-white/75 text-[15px] max-w-[480px] mx-auto">
              Chaque chantier est différent. On se déplace gratuitement, on évalue le support et on vous remet un devis détaillé. Sans engagement.
            </p>
            <Link to="/devis" className="btn-base btn-white mt-8" data-testid="service-tarif-devis-btn">
              Obtenir mon chiffre exact
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-28 bg-surface-canvas" data-testid="service-faq">
        <div className="max-w-[780px] mx-auto px-5 lg:px-6">
          <SectionHeader label="Questions fréquentes" title="Vos questions sur ce service." />
          <Reveal>
            <FaqAccordion items={service.faq} testIdPrefix={`faq-${service.slug}`} />
          </Reveal>
        </div>
      </section>

      {/* Bloc SEO local */}
      <section className="py-12 bg-surface-muted border-t border-line">
        <div className="max-w-[900px] mx-auto px-5 lg:px-6">
          <div className="space-y-4">
            {service.seoText.split("\n\n").map((paragraphe, i) => (
              <p key={i} className="text-[14.5px] text-ink-secondary leading-relaxed">{paragraphe}</p>
            ))}
          </div>
          <p className="mt-4 text-[14.5px]">
            <Link to="/zones-intervention" className="text-brand-royal font-medium hover:underline">Voir toutes nos zones d'intervention →</Link>
          </p>
        </div>
      </section>

      <CtaFinal />
    </>
  );
}
